// Client-side (100% free) meeting recorder.
// Composites all LiveKit participant tiles onto a <canvas>, mixes all audio
// tracks with WebAudio, records with MediaRecorder and streams the .webm
// straight to the user's disk via the File System Access API (no server,
// no LiveKit Cloud Egress fees).
import { RoomEvent, Track } from "livekit-client";

export const REC_TOPIC = "recording";

export function isRecordingSupported() {
  return (
    typeof MediaRecorder !== "undefined" &&
    typeof HTMLCanvasElement !== "undefined" &&
    !!HTMLCanvasElement.prototype.captureStream
  );
}

const initials = (label) =>
  (label || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const sanitize = (s) =>
  (s || "meeting").replace(/[^\p{L}\p{N} _-]/gu, "").trim().replace(/\s+/g, "_").slice(0, 60) || "meeting";

const dateStamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}`;
};

export class MeetingRecorder {
  constructor(room, { title } = {}) {
    this.room = room;
    this.title = title || "meeting";
    this.W = 1280;
    this.H = 720;
    this.fps = 24;
    this.recording = false;
    this.videoEls = new Map(); // trackSid -> HTMLVideoElement
    this.audioNodes = new Map(); // trackSid -> MediaStreamAudioSourceNode
  }

  async start() {
    const fname = `${sanitize(this.title)}_${dateStamp()}.webm`;
    this.fname = fname;
    this.writable = null;
    this.chunks = [];
    this._writeQ = Promise.resolve();
    if (window.showSaveFilePicker) {
      // Streams chunks straight to disk → long meetings never blow up RAM.
      const handle = await window.showSaveFilePicker({
        suggestedName: fname,
        types: [{ description: "WebM video", accept: { "video/webm": [".webm"] } }],
      });
      this.writable = await handle.createWritable();
    }

    this.canvas = document.createElement("canvas");
    this.canvas.width = this.W;
    this.canvas.height = this.H;
    this.ctx = this.canvas.getContext("2d");

    const AC = window.AudioContext || window.webkitAudioContext;
    this.audioCtx = new AC();
    if (this.audioCtx.state === "suspended") await this.audioCtx.resume().catch(() => {});
    this.audioDest = this.audioCtx.createMediaStreamDestination();

    const stream = this.canvas.captureStream(this.fps);
    const at = this.audioDest.stream.getAudioTracks()[0];
    if (at) stream.addTrack(at);

    const mime =
      ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"].find(
        (m) => MediaRecorder.isTypeSupported(m),
      ) || "";
    this.recorder = new MediaRecorder(stream, {
      ...(mime ? { mimeType: mime } : {}),
      videoBitsPerSecond: 2500000,
      audioBitsPerSecond: 128000,
    });
    this.recorder.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      if (this.writable) {
        this._writeQ = this._writeQ.then(() => this.writable.write(e.data)).catch(() => {});
      } else {
        this.chunks.push(e.data);
      }
    };
    this._stopPromise = new Promise((res) => {
      this.recorder.onstop = res;
    });

    this.startedAt = Date.now();
    this.recording = true;
    this._onRoomDisconnected = () => {
      this.stop().catch(() => {});
    };
    this.room.on(RoomEvent.Disconnected, this._onRoomDisconnected);

    this._drawTimer = setInterval(() => this._draw(), Math.round(1000 / this.fps));
    this._broadcast(true);
    this._hbTimer = setInterval(() => this._broadcast(true), 3000);
    this.recorder.start(1000);
  }

  getElapsedSec() {
    return this.recording ? Math.floor((Date.now() - this.startedAt) / 1000) : 0;
  }

  async stop() {
    if (!this.recording) return null;
    this.recording = false;
    clearInterval(this._drawTimer);
    clearInterval(this._hbTimer);
    this.room.off(RoomEvent.Disconnected, this._onRoomDisconnected);
    this._broadcast(false);
    try {
      this.recorder.stop();
      await this._stopPromise;
    } catch {
      /* already stopped */
    }
    // Tear down media graph.
    this.videoEls.forEach((el) => {
      try {
        el.srcObject = null;
      } catch {}
    });
    this.videoEls.clear();
    this.audioNodes.forEach((n) => {
      try {
        n.disconnect();
      } catch {}
    });
    this.audioNodes.clear();
    try {
      await this.audioCtx.close();
    } catch {}

    if (this.writable) {
      await this._writeQ;
      await this.writable.close().catch(() => {});
    } else {
      const blob = new Blob(this.chunks, { type: "video/webm" });
      this.chunks = [];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = this.fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    }
    return { fileName: this.fname };
  }

  _broadcast(on) {
    try {
      const by = this.room?.localParticipant?.name || this.room?.localParticipant?.identity || "";
      const payload = new TextEncoder().encode(JSON.stringify({ t: "rec", on, by }));
      this.room.localParticipant.publishData(payload, { reliable: true, topic: REC_TOPIC });
    } catch {
      /* room may be disconnecting */
    }
  }

  _videoElFor(pub) {
    const sid = pub.trackSid;
    const track = pub.videoTrack || pub.track;
    if (!track) return null;
    let el = this.videoEls.get(sid);
    if (!el) {
      el = track.attach();
      el.muted = true;
      el.play?.().catch(() => {});
      this.videoEls.set(sid, el);
    }
    return el;
  }

  _ensureAudio(pub, seen) {
    const sid = pub?.trackSid;
    const track = pub?.audioTrack || pub?.track;
    if (!sid || !track || !track.mediaStreamTrack || pub.isMuted) return;
    seen.add(sid);
    if (this.audioNodes.has(sid)) return;
    try {
      const src = this.audioCtx.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
      src.connect(this.audioDest);
      this.audioNodes.set(sid, src);
    } catch {}
  }

  _collect() {
    const parts = [this.room.localParticipant, ...this.room.remoteParticipants.values()];
    const cams = [];
    let screen = null;
    const seenVideo = new Set();
    const seenAudio = new Set();
    for (const p of parts) {
      const label = (p.name || p.identity || "").trim();
      const camPub = p.getTrackPublication(Track.Source.Camera);
      let el = null;
      if (camPub && !camPub.isMuted && (camPub.videoTrack || camPub.track)) {
        el = this._videoElFor(camPub);
        if (el) seenVideo.add(camPub.trackSid);
      }
      cams.push({ label, el });
      const ssPub = p.getTrackPublication(Track.Source.ScreenShare);
      if (!screen && ssPub && !ssPub.isMuted && (ssPub.videoTrack || ssPub.track)) {
        const sel = this._videoElFor(ssPub);
        if (sel) {
          seenVideo.add(ssPub.trackSid);
          screen = { label, el: sel };
        }
      }
      this._ensureAudio(p.getTrackPublication(Track.Source.Microphone), seenAudio);
      this._ensureAudio(p.getTrackPublication(Track.Source.ScreenShareAudio), seenAudio);
    }
    // GC stale attachments (participant left / camera off / mic muted).
    for (const [sid, el] of this.videoEls) {
      if (!seenVideo.has(sid)) {
        try {
          el.srcObject = null;
        } catch {}
        this.videoEls.delete(sid);
      }
    }
    for (const [sid, node] of this.audioNodes) {
      if (!seenAudio.has(sid)) {
        try {
          node.disconnect();
        } catch {}
        this.audioNodes.delete(sid);
      }
    }
    return { cams, screen };
  }

  _draw() {
    const { ctx, W, H } = this;
    ctx.fillStyle = "#0d1b2a";
    ctx.fillRect(0, 0, W, H);
    const { cams, screen } = this._collect();
    if (screen) {
      const stripH = cams.length ? 150 : 0;
      this._tile(screen, 0, 0, W, H - stripH, true);
      if (cams.length) {
        const tw = Math.min(240, Math.floor(W / cams.length));
        const x0 = Math.floor((W - tw * cams.length) / 2);
        cams.forEach((c, i) => this._tile(c, x0 + i * tw, H - stripH, tw, stripH, false));
      }
    } else {
      const n = Math.max(cams.length, 1);
      const cols = Math.ceil(Math.sqrt(n));
      const rows = Math.ceil(n / cols);
      const tw = W / cols;
      const th = H / rows;
      cams.forEach((c, i) => this._tile(c, (i % cols) * tw, Math.floor(i / cols) * th, tw, th, false));
    }
    this._overlay();
  }

  _tile(c, x, y, w, h, contain) {
    const { ctx } = this;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x + 2, y + 2, w - 4, h - 4);
    ctx.clip();
    const el = c.el;
    if (el && el.videoWidth > 0) {
      const s = contain
        ? Math.min((w - 4) / el.videoWidth, (h - 4) / el.videoHeight)
        : Math.max((w - 4) / el.videoWidth, (h - 4) / el.videoHeight);
      const dw = el.videoWidth * s;
      const dh = el.videoHeight * s;
      if (contain) {
        ctx.fillStyle = "#000";
        ctx.fillRect(x + 2, y + 2, w - 4, h - 4);
      }
      ctx.drawImage(el, x + 2 + (w - 4 - dw) / 2, y + 2 + (h - 4 - dh) / 2, dw, dh);
    } else {
      ctx.fillStyle = "#16283a";
      ctx.fillRect(x + 2, y + 2, w - 4, h - 4);
      const r = Math.min(w, h) * 0.18;
      ctx.beginPath();
      ctx.arc(x + w / 2, y + h / 2, r, 0, 7);
      ctx.fillStyle = "#0b1620";
      ctx.fill();
      ctx.strokeStyle = "#C8A96A";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "#C8A96A";
      ctx.font = `700 ${Math.max(14, Math.round(r * 0.8))}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initials(c.label), x + w / 2, y + h / 2);
    }
    if (c.label) {
      ctx.font = "600 13px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const tw2 = ctx.measureText(c.label).width;
      ctx.fillStyle = "rgba(11,22,32,0.75)";
      ctx.fillRect(x + 8, y + h - 32, tw2 + 16, 22);
      ctx.fillStyle = "#e7ecf1";
      ctx.fillText(c.label, x + 16, y + h - 21);
    }
    ctx.restore();
  }

  _overlay() {
    const { ctx } = this;
    const sec = this.getElapsedSec();
    const p = (n) => String(n).padStart(2, "0");
    const hh = Math.floor(sec / 3600);
    const label = `REC ${hh > 0 ? hh + ":" : ""}${p(Math.floor((sec % 3600) / 60))}:${p(sec % 60)}`;
    ctx.save();
    ctx.font = "700 15px sans-serif";
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = "rgba(11,22,32,0.8)";
    ctx.fillRect(12, 12, tw + 40, 30);
    if (Math.floor(Date.now() / 700) % 2 === 0) {
      ctx.beginPath();
      ctx.arc(28, 27, 6, 0, 7);
      ctx.fillStyle = "#e53935";
      ctx.fill();
    }
    ctx.fillStyle = "#fff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, 42, 28);
    ctx.restore();
  }
}
